import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, CalendarClock, Check, CircleAlert, Clock3, Contact, Hourglass, Inbox, Plus, RefreshCw, UserRoundPlus, Users, Workflow,
} from 'lucide-react';

import { AuthContext } from 'src/context/AuthContext';
import StagePage from 'src/stage/StagePage';
import { Monogram, Skeleton, firstName, formatNumber, greetingFor } from 'src/stage/StageUi';
import { characterFor } from 'src/stage/characters';
import { useWorkspace } from 'src/features/MiniCRM/Workspace/context/WorkspaceContext';
import { getErrorMessage, getWorkspaceOverview } from 'src/features/MiniCRM/Workspace/services/workspaceService';
import { clearLeads, clearPipelineAndLeadsCache } from 'src/features/MiniCRM/CachedData';
import { QUICK_ADD_EVENT } from './shared/CommandPalette';
import { listFollowUps, stageTones } from './shared/crmWork';
import { GettingStarted, UpNext, YourDay, isDueToday } from './HomeToday';
import HomeTargets from './targets/HomeTargets';
import { QUEUE_COPY, combineInsights, fetchWorkspaceInsights, formatPercent, leadQueueState } from './crmInsights';
import './home.css';

const SELECTED_PIPELINE_KEY = 'selectedPipelineId';
const QUEUE_ICONS = { overdue: CircleAlert, due_today: CalendarClock, unassigned: UserRoundPlus, untouched: Inbox, without_next_activity: Hourglass, stale: Clock3, today: Contact };

function todayLabel(workspaceName) {
  const date = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
  return workspaceName ? `${date} · ${workspaceName}` : date;
}

function KpiBar({ loading, insights, overview }) {
  if (loading && !insights) {
    return <section className="ws-card ws-kpis" aria-label="CRM summary">{[0, 1, 2, 3].map((index) => <div className="ws-kpi" key={index}><Skeleton width={110} height={12} /><Skeleton width={90} height={26} /></div>)}</section>;
  }
  const metrics = insights?.metrics;
  const change = metrics?.new_leads_change_percent;
  const due = (metrics?.due_today || 0) + (metrics?.overdue_activities || 0);
  const unassigned = overview?.stats?.leads?.unassigned;
  return (
    <section className="ws-card ws-kpis ws-rise" aria-label="CRM summary">
      <Link className="ws-kpi" to="/crm">
        <span className="ws-kpi-label">Open leads</span>
        <span className="ws-kpi-value">{metrics ? formatNumber(metrics.open_leads) : '—'} {metrics && <small>of {formatNumber(metrics.total_leads)}</small>}</span>
        <span className="ws-muted">{unassigned ? `${formatNumber(unassigned)} still need an owner` : 'Every lead has an owner'}</span>
      </Link>
      <Link className="ws-kpi" to="/insights">
        <span className="ws-kpi-label">New leads · {insights?.period?.days || 30} days</span>
        <span className="ws-kpi-value">{metrics ? formatNumber(metrics.new_leads) : '—'}</span>
        <span className={`ws-muted crm-home-change${change > 0 ? ' is-up' : change < 0 ? ' is-down' : ''}`}>
          {change === null || change === undefined ? 'No earlier period to compare' : `${change > 0 ? '+' : ''}${formatPercent(change)} vs previous ${insights?.period?.days || 30} days`}
        </span>
      </Link>
      <Link className="ws-kpi" to="/insights">
        <span className="ws-kpi-label">Won</span>
        <span className="ws-kpi-value">{metrics ? formatNumber(metrics.won_leads) : '—'} {metrics?.win_rate !== null && metrics?.win_rate !== undefined && <small>{formatPercent(metrics.win_rate)} win rate</small>}</span>
        {metrics && <div className="ws-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={metrics.win_rate || 0} aria-label="Win rate"><span style={{ width: `${Math.min(100, metrics.win_rate || 0)}%`, background: 'linear-gradient(90deg, #3FBF94, #1B7A5A)' }} /></div>}
      </Link>
      <div className="ws-kpi">
        <span className="ws-kpi-label" style={metrics?.overdue_activities ? { color: '#8F5B17' } : undefined}>{metrics?.overdue_activities ? <span className="ws-status ws-status--warning" aria-hidden="true" /> : null}Follow-ups due</span>
        <span className="ws-kpi-value">{metrics ? formatNumber(due) : '—'}</span>
        <span className="ws-muted">{metrics ? (metrics.overdue_activities ? `${formatNumber(metrics.overdue_activities)} overdue · ${formatNumber(metrics.due_today)} today` : `${formatNumber(metrics.due_today)} today, nothing overdue`) : 'Unavailable right now'}</span>
      </div>
    </section>
  );
}

function Spotlight({ loading, insights, onOpenQueue }) {
  if (loading && !insights) return <section className="ws-spot crm-home-spot"><Skeleton width={120} height={12} style={{ opacity: 0.3 }} /></section>;
  const top = insights?.queues?.find((queue) => ['overdue', 'due_today', 'unassigned'].includes(queue.key));
  const appy = characterFor('appy');
  const nila = characterFor('nila');
  if (top) {
    const total = insights.queues.filter((queue) => queue.key === top.key).reduce((count, queue) => count + queue.count, 0);
    const copy = {
      overdue: [`${total} ${total === 1 ? 'lead has' : 'leads have'} overdue follow-ups.`, 'Promised calls and meetings slipped. Clear these before new work so nobody goes cold.'],
      due_today: [`${total} follow-${total === 1 ? 'up is' : 'ups are'} due today.`, 'Work through today’s calls and meetings, then log what happened on each lead.'],
      unassigned: [`${total} ${total === 1 ? 'lead needs' : 'leads need'} an owner.`, 'Leads without an owner wait longest. Assign them so someone picks them up today.'],
    }[top.key];
    return (
      <section className="ws-spot crm-home-spot ws-rise">
        <div className="ws-spot-art crm-home-spot-halo" aria-hidden="true" />
        {nila?.full && <img className="ws-spot-art crm-home-spot-art" src={nila.full} alt="" aria-hidden="true" />}
        <span className="ws-eyebrow">Needs you · {top.pipeline?.name}</span>
        <h2 className="ws-spot-copy crm-home-spot-title">{copy[0]}</h2>
        <p className="ws-muted ws-spot-copy crm-home-spot-text">{copy[1]}</p>
        <div className="crm-home-spot-actions">
          <button type="button" className="ws-btn ws-btn--white ws-btn--sm" onClick={() => onOpenQueue(top)}>Review {top.count} {top.count === 1 ? 'lead' : 'leads'}<ArrowRight size={14} /></button>
          <Link className="ws-btn ws-btn--glass ws-btn--sm" to="/insights">See insights</Link>
        </div>
      </section>
    );
  }
  return (
    <section className="ws-spot crm-home-spot ws-rise">
      <div className="ws-spot-art crm-home-spot-halo" aria-hidden="true" />
      {appy?.full && <img className="ws-spot-art crm-home-spot-art" src={appy.full} alt="" aria-hidden="true" />}
      <span className="ws-eyebrow">All clear</span>
      <h2 className="ws-spot-copy crm-home-spot-title">Every lead has an owner and nothing is overdue.</h2>
      <p className="ws-muted ws-spot-copy crm-home-spot-text">{insights ? 'New enquiries and slipped follow-ups will show up here first.' : 'We couldn’t check your queues. The lead list has everything.'}</p>
      <div className="crm-home-spot-actions">
        <Link className="ws-btn ws-btn--white ws-btn--sm" to="/crm">Open leads</Link>
      </div>
    </section>
  );
}

function QueueCard({ loading, insights, onOpenQueue }) {
  const groups = (insights?.queueGroups || []).filter((group) => group.key !== 'today').slice(0, 5);
  return (
    <section className="ws-card ws-card-pad crm-home-card ws-rise">
      <div className="ws-card-head"><h2 className="ws-h3 ws-grow">Needs attention</h2><Link className="crm-link" to="/crm">All leads</Link></div>
      {loading && !insights ? [0, 1, 2, 3].map((index) => <div className="ws-row" key={index}><Skeleton width={34} height={34} radius={11} /><div className="ws-grow"><Skeleton width="60%" /><Skeleton width="40%" height={10} style={{ marginTop: 6 }} /></div></div>)
        : groups.length === 0 ? (
          <div className="ws-empty crm-home-empty"><Check size={20} color="#1B7A5A" /><strong>Nothing waiting</strong>Owners, next steps and follow-ups are all in place.</div>
        ) : (
          <ul className="crm-home-list">
            {groups.map((group) => {
              const Icon = QUEUE_ICONS[group.key] || Inbox;
              const tone = QUEUE_COPY[group.key]?.tone || 'blue';
              const where = group.parts.length > 1
                ? group.parts.map((part) => `${part.pipeline?.name} ${part.count}`).join(' · ')
                : `${group.top.pipeline?.name} · ${QUEUE_COPY[group.key]?.hint || ''}`;
              return (
                <li key={group.key}>
                  <button type="button" className="crm-home-queue" onClick={() => onOpenQueue(group.top)} title={group.parts.length > 1 ? `Opens ${group.top.pipeline?.name}, the pipeline with the most` : undefined}>
                    <span className={`ws-icon-tile crm-home-tile${tone === 'blue' ? '' : ` ws-icon-tile--${tone}`}`} aria-hidden="true"><Icon size={16} /></span>
                    <span className="ws-grow">
                      <span className="crm-home-row-title">{group.label}</span>
                      <span className="crm-home-row-sub">{where}</span>
                    </span>
                    <span className="crm-home-count ws-num">{formatNumber(group.count)}</span>
                    <ArrowRight size={15} className="crm-home-chev" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
    </section>
  );
}

function PipelineRow({ entry, canManage, onOpen }) {
  const { pipeline, data } = entry;
  const funnel = data?.funnel || [];
  const total = funnel.reduce((count, stage) => count + stage.count, 0);
  const open = funnel.filter((stage) => !stage.is_closed).reduce((count, stage) => count + stage.count, 0);
  const won = funnel.filter((stage) => stage.is_closed && stage.probability >= 100).reduce((count, stage) => count + stage.count, 0);
  const tones = stageTones(funnel);
  const filled = funnel.filter((stage) => stage.count > 0);
  return (
    <li className="crm-home-pipe">
      <div className="crm-home-pipe-head">
        <span className="ws-icon-tile crm-home-tile" aria-hidden="true"><Workflow size={16} /></span>
        <div className="ws-grow">
          <div className="crm-home-row-title">{pipeline.name}{pipeline.is_default && <span className="ws-chip crm-chip-sm">Default</span>}</div>
          <div className="crm-home-row-sub">{data ? `${formatNumber(open)} open · ${formatNumber(won)} won · ${pipeline.stages_count ?? funnel.length} stages` : 'Numbers unavailable right now'}</div>
        </div>
        <div className="crm-home-pipe-actions">
          {canManage && <Link className="ws-btn ws-btn--plain" to={`/admin/pipeline/${pipeline.id}`}>Stages</Link>}
          <button type="button" className="ws-agent-open" onClick={() => onOpen(pipeline)}>Leads <ArrowUpRight size={13} /></button>
        </div>
      </div>
      {total > 0 ? (
        <>
          <div className="crm-home-bar" role="img" aria-label={funnel.map((stage) => `${stage.name}: ${stage.count}`).join(', ')}>
            {filled.map((stage) => <span key={stage.id} style={{ flexGrow: stage.count, background: tones[stage.id]?.color }} title={`${stage.name} · ${stage.count}`} />)}
          </div>
          <div className="crm-home-legend">
            {filled.map((stage) => <span key={stage.id}><i style={{ background: tones[stage.id]?.color }} />{stage.name} <b className="ws-num">{stage.count}</b></span>)}
          </div>
        </>
      ) : (
        <div className="crm-home-bar is-empty"><span /></div>
      )}
    </li>
  );
}

function PipelinesCard({ loading, insights, overview, canManage, onOpen }) {
  const entries = insights?.pipelines || (overview?.pipelines || []).map((pipeline) => ({ pipeline, data: null }));
  return (
    <section className="ws-card ws-card-pad crm-home-card ws-rise">
      <div className="ws-card-head"><h2 className="ws-h3 ws-grow">Pipelines</h2>{canManage && <Link className="crm-link" to="/admin/pipelines">Manage</Link>}</div>
      <p className="ws-muted" style={{ marginBottom: 6 }}>Where every lead sits right now, stage by stage.</p>
      {loading && !entries.length ? [0, 1].map((index) => <div key={index} style={{ padding: '14px 0' }}><Skeleton width="45%" /><Skeleton height={10} style={{ marginTop: 12 }} /></div>)
        : entries.length === 0 ? (
          <div className="ws-empty crm-home-empty"><Workflow size={20} color="#5B7180" /><strong>No pipelines yet</strong>A pipeline holds your stages and the lead table.{canManage && <Link className="ws-btn ws-btn--primary ws-btn--sm" style={{ marginTop: 8 }} to="/admin/pipelines?create=1"><Plus size={14} />Create pipeline</Link>}</div>
        ) : (
          <ul className="crm-home-list">
            {entries.map((entry) => <PipelineRow key={entry.pipeline.id} entry={entry} canManage={canManage} onOpen={onOpen} />)}
          </ul>
        )}
    </section>
  );
}

function TeamCard({ loading, insights, overview, canViewTeam }) {
  const team = (insights?.team || []).slice(0, 5);
  const stats = overview?.stats?.members;
  return (
    <section className="ws-card ws-card-pad crm-home-card ws-rise">
      <div className="ws-card-head"><h2 className="ws-h3 ws-grow">Team this month</h2>{canViewTeam && <Link className="crm-link" to="/teams">Team</Link>}</div>
      <p className="ws-muted" style={{ marginBottom: 6 }}>{stats ? `${formatNumber(stats.active)} active of ${formatNumber(stats.total)} people` : 'Who is working the leads.'}</p>
      {loading && !insights ? [0, 1, 2].map((index) => <div className="ws-row" key={index}><Skeleton width={32} height={32} radius={16} /><div className="ws-grow"><Skeleton width="50%" /></div></div>)
        : team.length === 0 ? (
          <div className="ws-empty crm-home-empty"><Users size={20} color="#5B7180" /><strong>No activity yet</strong>Calls, messages and meetings logged on leads count here.</div>
        ) : (
          <ul className="crm-home-list">
            {team.map((member) => (
              <li key={member.user.id} className="crm-home-person">
                <Monogram name={member.user.name} size={32} />
                <span className="ws-grow">
                  <span className="crm-home-row-title">{member.user.name}</span>
                  <span className="crm-home-row-sub">{formatNumber(member.assigned_leads)} leads · {formatNumber(member.completed_activities)} of {formatNumber(member.activities)} done</span>
                </span>
                <span className="crm-home-rate">
                  <span className="ws-num">{member.completion_rate}%</span>
                  <span className="ws-meter" style={{ width: 64 }}><span style={{ width: `${member.completion_rate}%` }} /></span>
                </span>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { currentWorkspace, currentWorkspaceId } = useWorkspace();
  const workspaceId = currentWorkspaceId || currentWorkspace?.id;
  const [overview, setOverview] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [work, setWork] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getWorkspaceOverview(workspaceId);
      setOverview(data);
      const pipelines = (data?.pipelines || []).filter((pipeline) => pipeline.status !== 'archived');
      const rows = await fetchWorkspaceInsights(pipelines, { days: 30 });
      setInsights(combineInsights(rows));
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'We couldn’t load your workspace.'));
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  // My follow-ups, earliest first (overdue, today, then upcoming), plus how many
  // are planned across the workspace for the setup guide.
  const loadWork = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [mine, all] = await Promise.all([listFollowUps({ mine: true, perPage: 12 }), listFollowUps({ perPage: 1 })]);
      setWork({ items: mine.items, total: Number(mine.meta.total ?? mine.items.length) || 0, planned: Number(all.meta.total ?? all.items.length) || 0 });
    } catch {
      setWork({ items: [], total: 0, planned: 0, failed: true });
    }
  }, [workspaceId]);

  useEffect(() => { load(); loadWork(); }, [load, loadWork]);
  useEffect(() => {
    const onLeads = () => { load(); loadWork(); };
    window.addEventListener('crm:followups-changed', loadWork);
    window.addEventListener('leads-reset', onLeads);
    return () => { window.removeEventListener('crm:followups-changed', loadWork); window.removeEventListener('leads-reset', onLeads); };
  }, [load, loadWork]);

  const capabilities = overview?.capabilities || {};
  const openQueue = useCallback((queue) => {
    clearLeads();
    if (queue.pipeline?.id) localStorage.setItem(SELECTED_PIPELINE_KEY, queue.pipeline.id);
    navigate('/crm', { state: leadQueueState(queue) });
  }, [navigate]);

  const openPipeline = useCallback((pipeline) => {
    localStorage.setItem(SELECTED_PIPELINE_KEY, pipeline.id);
    clearPipelineAndLeadsCache();
    navigate('/crm');
  }, [navigate]);

  const metrics = insights?.metrics;
  const subtitle = metrics
    ? (metrics.overdue_activities || metrics.due_today
      ? `${formatNumber(metrics.due_today)} follow-ups today${metrics.overdue_activities ? ` and ${formatNumber(metrics.overdue_activities)} overdue` : ''} across ${insights.pipelines.length} ${insights.pipelines.length === 1 ? 'pipeline' : 'pipelines'}.`
      : `No follow-ups due. ${formatNumber(metrics.new_leads)} new leads in the last ${insights.period?.days || 30} days.`)
    : 'Your leads, follow-ups and pipelines at a glance.';

  return (
    <StagePage
      eyebrow={todayLabel(currentWorkspace?.name)}
      title={`${greetingFor()}, ${firstName(currentUser?.name) || 'there'}.`}
      subtitle={subtitle}
      actions={(
        <>
          <button type="button" className="crm-icon-btn" onClick={() => { load(); loadWork(); }} disabled={loading} aria-label="Refresh" title="Refresh"><RefreshCw size={16} className={loading ? 'ws-spin' : ''} /></button>
          <Link className="ws-btn ws-btn--quiet" to="/crm"><Contact size={15} />Open leads</Link>
          <button type="button" className="ws-btn ws-btn--primary" onClick={() => window.dispatchEvent(new Event(QUICK_ADD_EVENT))} disabled={!overview?.pipelines?.length}><Plus size={16} />Add lead</button>
        </>
      )}
    >
      {error && (
        <div className="ws-notice" role="alert">
          <CircleAlert size={16} style={{ flex: 'none', marginTop: 1 }} />
          <span className="ws-grow">{error} Nothing is shown as zero.</span>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={load}>Try again</button>
        </div>
      )}
      {insights?.partial && <div className="ws-notice" role="status"><CircleAlert size={16} style={{ flex: 'none', marginTop: 1 }} />Some pipelines didn’t load, so totals may be low. Refresh to try again.</div>}

      <KpiBar loading={loading} insights={insights} overview={overview} />
      <GettingStarted key={workspaceId} workspaceId={workspaceId} overview={overview} capabilities={capabilities} followUpTotal={work?.planned || 0} />
      <HomeTargets me={currentUser?.id} />

      <div className="crm-home-row-3">
        <UpNext
          loading={!work}
          item={work ? (isDueToday(work.items[0]) ? work.items[0] : null) : undefined}
          fallback={<Spotlight loading={loading} insights={insights} onOpenQueue={openQueue} />}
          onChanged={loadWork}
        />
        <YourDay loading={!work} items={work?.items} total={work?.total} skipId={isDueToday(work?.items?.[0]) ? work.items[0].id : null} onChanged={loadWork} />
        <QueueCard loading={loading} insights={insights} onOpenQueue={openQueue} />
      </div>

      <div className="crm-home-row-2">
        <PipelinesCard loading={loading} insights={insights} overview={overview} canManage={Boolean(capabilities.can_manage_workflows)} onOpen={openPipeline} />
        <TeamCard loading={loading} insights={insights} overview={overview} canViewTeam={Boolean(capabilities.can_manage_members) || overview?.role === 'manager'} />
      </div>

    </StagePage>
  );
}
